import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import type CreateEmailMessageViewModel from "../../types/viewModels/CreateEmailMessageViewModel";
import type { EmailMessageDTO } from "../../types/dtos/EmailMessageDTO";
import { objectToFormData } from "../../helpers/objectToFormData";
import type { RecordId } from "@repo/types/ui/tables";
import type { ExecutionResponse } from "@repo/types/responses";
import { useApiAccess } from "@repo/hooks/contexts";
import { ticketEmailMessageKeys } from "@repo/types/queries";

export default function useGetTicketEmailMessages(ticketId: RecordId) {
  const { apiClient } = useApiAccess();

  return useQuery({
    queryKey: ticketEmailMessageKeys.listByRelationId(ticketId),
    queryFn: async () => {
      const response = await apiClient.get<EmailMessageDTO[]>(
        `/emailMessage/getByTicketId/${ticketId}`,
      );
      return response.data;
    },
    enabled: !!ticketId,
  });
}

export function useSendEmailMessage(ticketGuid: string): UseMutationResult<
  ExecutionResponse, // ✅ response type (data)
  Error, // ✅ error type
  { model: CreateEmailMessageViewModel } // ✅ variables type
> {
  const { apiClient } = useApiAccess();
  const queryClient = useQueryClient();

  const mutation = useMutation<
    ExecutionResponse,
    Error,
    { model: CreateEmailMessageViewModel }
  >({
    mutationFn: async ({ model }) => {
      const formData = objectToFormData(model);

      const response = await apiClient.post<ExecutionResponse>(
        "/emailMessage/send",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      return response.data;
    },
    onSuccess: () => {
      ticketEmailMessageKeys.invalidateListByRelationId(
        queryClient,
        ticketGuid,
      );
    },
  });

  return mutation;
}
